// The stale-bundle guard. After a deploy, a tab (or a phone that kept the page
// open) still runs the OLD index and asks for chunk files that no longer exist:
// every lazy part of the site (FaceLock Scan, its models) then fails to load,
// or worse, quietly runs old code. When a chunk fails to load the page reloads
// ONCE, which fetches the new index and its chunks. Once only: a second
// failure within RELOAD.againAfterMs is left to surface (a real outage must
// not become a reload loop).
//
// Vite fires "vite:preloadError" on window when a dynamic import's chunk (or
// its CSS) cannot be fetched; browsers that fail the import itself reject it
// with one of the messages in CHUNK_ERROR.

export const RELOAD = {
  key: "stale_bundle_reload_at",
  /** A reload this recent already happened: do not reload again. */
  againAfterMs: 5 * 60 * 1000,
} as const;

const CHUNK_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Loading (?:CSS )?chunk [\w-]+ failed/i;

/** Is this the error of a chunk that would not load (a stale bundle after a deploy)? */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: unknown; message?: unknown };
  const text = `${typeof e.name === "string" ? e.name : ""} ${typeof e.message === "string" ? e.message : typeof err === "string" ? err : ""}`;
  return CHUNK_ERROR.test(text);
}

type Store = Pick<Storage, "getItem" | "setItem">;

/** True (and remembered) when a reload is allowed now; false when one already happened within RELOAD.againAfterMs. */
export function takeReload(store: Store | null, now: number): boolean {
  if (!store) return false;
  try {
    const last = Number(store.getItem(RELOAD.key) || 0);
    if (Number.isFinite(last) && last > 0 && now - last < RELOAD.againAfterMs) return false;
    store.setItem(RELOAD.key, String(now));
    return true;
  } catch {
    // No storage (private mode): a reload could not be remembered, so none is made.
    return false;
  }
}

export interface GuardOptions {
  win?: Window;
  reload?: () => void;
  now?: () => number;
  store?: Store | null;
}

/** Listen for chunk-load failures and reload once. Returns the function that removes the listeners. */
export function installStaleBundleGuard(opts: GuardOptions = {}): () => void {
  const win = opts.win ?? (typeof window !== "undefined" ? window : undefined);
  if (!win) return () => {};
  const now = opts.now ?? (() => Date.now());
  const store = opts.store !== undefined ? opts.store : (() => { try { return win.sessionStorage; } catch { return null; } })();
  const reload = opts.reload ?? (() => win.location.reload());
  const tryReload = (): boolean => {
    if (!takeReload(store, now())) return false;
    try { reload(); } catch { /* nothing more to do */ }
    return true;
  };
  const onPreload = (e: Event) => {
    // Handled (the page is reloading): Vite then does not throw the error as well.
    if (tryReload()) e.preventDefault();
  };
  const onRejection = (e: PromiseRejectionEvent) => {
    if (isChunkLoadError(e.reason)) tryReload();
  };
  win.addEventListener("vite:preloadError", onPreload);
  win.addEventListener("unhandledrejection", onRejection);
  return () => {
    win.removeEventListener("vite:preloadError", onPreload);
    win.removeEventListener("unhandledrejection", onRejection);
  };
}
